<template>
  <!-- Contenido del split (sin el chrome del modal; se envuelve en EpcModalWrapper desde el padre). -->
  <div v-if="target" class="boq-split">
    <p class="boq-split__lead">
      {{ $t('split_measurement_intro', { name: target.name || target.code || ('#' + target.id) }) }}
    </p>
    <div class="boq-split__figures">
      <div class="boq-split__fig">
        <span class="boq-split__fig-label">{{ $t('quantity') }}</span>
        <span class="boq-split__fig-val">{{ formatQty(target.quantity) }}</span>
      </div>
      <b-icon pack="fas" icon="arrow-right" size="is-small" class="boq-split__arrow" />
      <div class="boq-split__fig boq-split__fig--keep">
        <span class="boq-split__fig-label">{{ $t('split_kept') }}</span>
        <span class="boq-split__fig-val">{{ formatQty(covered) }}</span>
      </div>
      <span class="boq-split__plus">+</span>
      <div class="boq-split__fig boq-split__fig--new">
        <span class="boq-split__fig-label">{{ $t('split_new') }}</span>
        <span class="boq-split__fig-val">{{ formatQty(excess) }}</span>
      </div>
    </div>

    <!-- Previsualización de la nueva medición (clon del excedente). Solo el código es editable. -->
    <div class="boq-split__form">
      <div class="boq-split__form-title">
        {{ $t('split_new') }}
        <b-icon v-if="loading" pack="fas" icon="spinner" size="is-small" class="fa-spin" />
      </div>

      <div class="flex flex-row">
        <div class="flex flex-col w-1/2 mr-2">
          <div class="text-xs text-blue font-medium">{{ $t('code') }}</div>
          <input v-model="form.code" name="split_code" class="boq-split__input" />
        </div>
        <div class="flex flex-col w-1/2 ml-2">
          <div class="text-xs text-blue font-medium">{{ $t('name') }}</div>
          <input :value="form.name" disabled class="boq-split__input" />
        </div>
      </div>

      <div class="flex flex-row mt-3">
        <div class="flex flex-col w-1/4">
          <div class="text-xs text-blue font-medium">{{ $t('measurement_unit') }}</div>
          <input :value="form.unit" disabled class="boq-split__input" />
        </div>
        <div class="flex flex-col w-1/4 mx-2">
          <div class="text-xs text-blue font-medium capitalize">{{ $t('units') }}</div>
          <input :value="form.total_units" disabled class="boq-split__input" />
        </div>
        <div class="flex flex-col w-1/4 mr-2">
          <div class="text-xs text-blue font-medium">{{ $t('unit_price') }}</div>
          <input :value="form.unit_price" disabled class="boq-split__input" />
        </div>
        <div class="flex flex-col w-1/4">
          <div class="text-xs text-blue font-medium">{{ $t('unit_cost') }}</div>
          <input :value="form.unit_cost" disabled class="boq-split__input" />
        </div>
      </div>

      <div class="flex flex-row mt-3">
        <div class="flex flex-col w-1/3">
          <div class="text-xs text-blue font-medium capitalize">{{ $t('length') }}</div>
          <input :value="form.length" disabled class="boq-split__input" />
        </div>
        <div class="flex flex-col w-1/3 mx-2">
          <div class="text-xs text-blue font-medium capitalize">{{ $t('width') }}</div>
          <input :value="form.width" disabled class="boq-split__input" />
        </div>
        <div class="flex flex-col w-1/3">
          <div class="text-xs text-blue font-medium capitalize">{{ $t('height') }}</div>
          <input :value="form.height" disabled class="boq-split__input" />
        </div>
      </div>

      <div class="flex flex-row mt-3">
        <div class="flex flex-col w-1/2 mr-2">
          <div class="text-xs text-blue font-medium">{{ $t('work_order') }}</div>
          <input :value="form.work_order_code" disabled class="boq-split__input" />
        </div>
        <div class="flex flex-col w-1/2 ml-2">
          <div class="text-xs text-blue font-medium capitalize">{{ $t('position') }}</div>
          <input :value="form.position" disabled class="boq-split__input" />
        </div>
      </div>
    </div>

    <div class="boq-split__note">
      <b-icon pack="fas" icon="circle-info" size="is-small" />
      <span>{{ $t('split_measurement_note') }}</span>
    </div>
  </div>
</template>

<script>
  import axios from "axios";

  export default {
    name: 'SplitAssignment',

    props: {
      // Medición excedente a dividir (con _coveredQty/_excessQty ya calculados en la fila).
      target: { type: Object, default: null },
      // Cantidad que se mantiene en la original y excedente que va al clon.
      covered: { type: Number, default: 0 },
      excess: { type: Number, default: 0 },
    },

    data() {
      return {
        loading: false,   // cargando el preview de la medición
        busy: false,      // ejecutando el split
        form: {
          code: '', name: '', unit: '', total_units: '', unit_price: '',
          unit_cost: '', length: '', width: '', height: '', work_order_code: '', position: '',
        },
      };
    },

    watch: {
      // Si cambia la medición objetivo, reinicia y recarga el preview.
      'target.id'() {
        this.initForm();
        this.loadPreview();
      },
    },

    created() {
      this.initForm();
      this.loadPreview();
    },

    methods: {
      // Precarga los campos que se replican en la nueva medición (sugerencia de código editable).
      initForm() {
        const m = this.target || {};
        this.form = {
          code: (m.code || '') + '-01',
          name: m.name || '',
          unit: m.unit || '',
          total_units: '',
          unit_price: '',
          unit_cost: '',
          length: '',
          width: '',
          height: '',
          work_order_code: '',
          position: '',
        };
      },

      // Trae la medición completa para previsualizar los campos que hereda el clon.
      async loadPreview() {
        if (!this.target || !this.target.id) return;
        this.loading = true;
        try {
          const { data } = await axios.get(`/api/v2/assignment/${this.target.id}`, { params: { with_order: 1 } });
          if (data && data.success && data.assignment) {
            const a = data.assignment;
            // Unidades del clon = excedente convertido (factor = total_quantity / total_units).
            const totalQty = Number(a.total_quantity) || 0;
            const totalUnits = Number(a.total_units) || 0;
            const factor = totalUnits !== 0 ? totalQty / totalUnits : 1;
            const excessUnits = factor !== 0 ? this.excess / factor : this.excess;
            this.form = {
              ...this.form,
              name: a.name || this.form.name,
              unit: (a.unit && a.unit.name) || this.form.unit,
              total_units: this.formatQty(excessUnits),
              unit_price: a.unit_price != null ? String(a.unit_price) : '',
              unit_cost: a.unit_cost != null ? String(a.unit_cost) : '',
              length: a.length != null ? String(a.length) : '',
              width: a.width != null ? String(a.width) : '',
              height: a.height != null ? String(a.height) : '',
              work_order_code: (a.order && a.order.code) || '',
              position: a.position != null ? String(a.position) : '',
            };
          }
        } catch (e) {
          // El preview es informativo; si falla, se mantiene lo precargado.
        } finally {
          this.loading = false;
        }
      },

      // Ejecuta el split. Público: lo invoca el botón del footer del modal (vía ref).
      // Emite 'busy' para que el padre deshabilite el botón, y 'saved' al terminar OK.
      async submit() {
        if (!this.target || this.busy) return;
        this.busy = true;
        this.$emit('busy', true);
        try {
          const { data } = await axios.post(
            `/api/v2/assignment/${this.target.id}/split`,
            { covered_quantity: this.covered, code: this.form.code }
          );
          if (data && data.success) {
            this.$notify.success('split_measurement_ok');
            this.$emit('saved');
          } else {
            this.$notify.error((data && data.message) || 'error');
          }
        } catch (e) {
          const msg = e && e.response && e.response.data && e.response.data.message;
          this.$notify.error(msg || 'error');
        } finally {
          this.busy = false;
          this.$emit('busy', false);
        }
      },

      formatQty(v) {
        const n = parseFloat(v);
        if (!Number.isFinite(n)) return '—';
        return n.toLocaleString('es-ES', { maximumFractionDigits: 4 });
      },
    },
  };
</script>

<style scoped>
.boq-split__lead { font-size: 0.9rem; color: #374151; margin-bottom: 0.85rem; line-height: 1.4; }
.boq-split__figures {
  display: flex;
  align-items: flex-end;
  justify-content: center;
  gap: 0.6rem;
  flex-wrap: wrap;
  padding: 0.75rem;
  background: #f7f9fc;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
}
.boq-split__fig {
  display: flex;
  flex-direction: column;
  align-items: center;
  min-width: 4.5rem;
  padding: 0.35rem 0.6rem;
  border-radius: 6px;
  background: #fff;
  border: 1px solid #e5e7eb;
}
.boq-split__fig--keep { border-color: rgba(39,174,96,.4); }
.boq-split__fig--new { border-color: rgba(58,107,200,.4); }
.boq-split__fig-label { font-size: 9px; font-weight: 700; text-transform: uppercase; letter-spacing: .03em; color: #6b7280; }
.boq-split__fig-val { font-size: 15px; font-weight: 700; color: #1f2937; font-variant-numeric: tabular-nums; }
.boq-split__arrow, .boq-split__plus { color: #9ca3af; padding-bottom: 0.5rem; }
.boq-split__plus { font-size: 18px; font-weight: 700; }
.boq-split__note {
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  margin-top: 0.85rem;
  padding: 0.6rem 0.75rem;
  border-radius: 8px;
  background: #fff7ed;
  border: 1px solid #fed7aa;
  color: #9a3412;
  font-size: 0.78rem;
  line-height: 1.4;
}
.boq-split__form {
  margin-top: 0.85rem;
  padding: 0.75rem;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background: #fff;
}
.boq-split__form-title {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  font-size: 0.72rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: .03em;
  color: rgb(58, 107, 200);
  margin-bottom: 0.6rem;
}
.boq-split__input {
  width: 100%;
  height: 2rem;
  padding: 0.25rem 0.4rem;
  font-size: 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  background: #f8fafc;
  color: #1f2937;
}
.boq-split__input:disabled { background: #eef1f6; color: #6b7280; cursor: not-allowed; }
.boq-split__input:not(:disabled):focus { outline: none; border-color: rgb(58, 107, 200); background: #fff; }
</style>
